import { defineStore } from 'pinia'
import type { InversedMapBounds, MapKeyGroup } from '#cmui/types/map'
import type { MapMarkersRequestPayload, MappyResponse } from '~/interfaces/map'
import { $fetchApi } from '~/composables/fetchApi'

const useMapStore = defineStore('map', () => {
    const loadingMap = ref(false)

    async function fetchKeyData(locale: string) {
        loadingMap.value = true

        return $fetchApi<MapKeyGroup[]>(
            `${useRuntimeConfig().public.APPLICATION_URL}/mappy/legend.json`,
            { query: { locale } }
        )
            .then((response) => response)
            .finally(() => {
                loadingMap.value = false
            })
    }

    async function fetchMapMarkers(
        bounds: InversedMapBounds,
        networkId: number | undefined = undefined,
        city: string | undefined = undefined,
        countryId: number | undefined = undefined,
        canChargeWithChargemap: boolean | undefined = undefined
    ) {
        if (
            !bounds?.NW?.lat ||
            !bounds?.NW?.lng ||
            !bounds?.SE?.lat ||
            !bounds?.SE?.lng
        ) {
            return {
                items: []
            }
        }

        const payload: MapMarkersRequestPayload = {
            NW: [`${bounds.NW.lat};${bounds.NW.lng}`],
            SE: [`${bounds.SE.lat};${bounds.SE.lng}`]
        }

        loadingMap.value = true

        const params: any = {
            network: networkId,
            state: [2]
        }

        if (city) {
            params.city = city
        }

        if (countryId) {
            params.country = countryId
        }

        if (city && countryId) {
            params.offset = 0
        }

        if (canChargeWithChargemap !== undefined) {
            params.can_charge_with_chargemap = canChargeWithChargemap
        }

        return $fetchApi<MappyResponse>(
            `${useRuntimeConfig().public.APPLICATION_URL}/mappy/charging_pools.json`,
            {
                query: {
                    ...payload,
                    ...params
                }
            }
        )
            .then((responseFromMappy) => ({
                items: responseFromMappy.response?.content?.items
            }))
            .finally(() => {
                loadingMap.value = false
            })
    }

    return {
        // Data
        loadingMap,

        // Methods
        fetchKeyData,
        fetchMapMarkers
    }
})

type MapStore = Omit<
    ReturnType<typeof useMapStore>,
    keyof ReturnType<typeof defineStore>
>

export default useMapStore

export type { MapStore }
